import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { arrayMove } from '@dnd-kit/sortable'
import API from '../../../../services/API'
import { KIND, ROW, rowForKind } from './chipTypes'

// Working-copy state for the load list builder.
//
// Shape of a working section:
//   { clientId, _id?, sectionName, sectionType,
//     requiredConsumables: [{ clientId, name, size, quantity, noExpiry }],
//     requiredAssets:      [{ clientId, category, subCategory, quantity }] }
//
// Legacy top-level `requiredAssets` on the document are folded into a
// 'General' section on load so saving migrates them into sections (this is
// a data migration, the schema is untouched - the server keeps accepting
// both shapes).

const makeId = () =>
  typeof crypto !== 'undefined' && crypto?.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(16).slice(2)}`

const normaliseQty = (q, fallback = 1) => {
  const n = Number(q)
  if (!Number.isFinite(n) || n < 1) return fallback
  return Math.floor(n)
}

const normaliseConsumable = (row = {}) => ({
  clientId: makeId(),
  name: String(row.name ?? ''),
  size: String(row.size ?? ''),
  quantity: normaliseQty(row.quantity),
  noExpiry: !!row.noExpiry,
})

const normaliseAsset = (row = {}) => ({
  clientId: makeId(),
  category: String(row.category ?? ''),
  subCategory: String(row.subCategory ?? ''),
  quantity: normaliseQty(row.quantity),
})

export const makeSection = (sectionName = '', sectionType = '') => ({
  clientId: makeId(),
  sectionName,
  sectionType,
  requiredConsumables: [],
  requiredAssets: [],
})

const normaliseSection = (s = {}) => ({
  clientId: makeId(),
  ...(s._id ? { _id: s._id } : {}),
  sectionName: String(s.sectionName ?? ''),
  sectionType: String(s.sectionType ?? ''),
  requiredConsumables: (Array.isArray(s.requiredConsumables)
    ? s.requiredConsumables
    : []
  ).map(normaliseConsumable),
  requiredAssets: (Array.isArray(s.requiredAssets) ? s.requiredAssets : []).map(
    normaliseAsset,
  ),
})

// Fold legacy document-level requiredAssets into a 'General' section (same
// rule the check panel uses to render them).
const sectionsFromDocument = (doc) => {
  const sections = (Array.isArray(doc?.sections) ? doc.sections : []).map(
    normaliseSection,
  )
  const legacy = (Array.isArray(doc?.requiredAssets) ? doc.requiredAssets : [])
    .filter((a) => String(a?.category || '').trim())
    .map(normaliseAsset)
  if (legacy.length === 0) return sections

  const generalIndex = sections.findIndex(
    (s) => s.sectionName.trim().toLowerCase() === 'general',
  )
  if (generalIndex === -1) {
    return [...sections, { ...makeSection('General'), requiredAssets: legacy }]
  }
  return sections.map((s, i) =>
    i === generalIndex
      ? { ...s, requiredAssets: [...s.requiredAssets, ...legacy] }
      : s,
  )
}

// Strip client-only fields so the working copy can be compared to the
// baseline and sent to the server.
export const serialiseSections = (sections) =>
  sections.map((s) => ({
    ...(s._id ? { _id: s._id } : {}),
    sectionName: s.sectionName,
    sectionType: s.sectionType,
    requiredConsumables: s.requiredConsumables.map((c) => ({
      name: c.name,
      size: c.size,
      quantity: normaliseQty(c.quantity),
      noExpiry: !!c.noExpiry,
    })),
    requiredAssets: s.requiredAssets.map((a) => ({
      category: a.category,
      subCategory: a.subCategory,
      quantity: normaliseQty(a.quantity),
    })),
  }))

const snapshot = ({ title, type, sections }) =>
  JSON.stringify({
    title: title.trim(),
    type,
    sections: serialiseSections(sections),
  })

export const kindForChip = (row, chip) => {
  if (row === ROW.CONSUMABLES) return KIND.CONSUMABLE
  return String(chip?.subCategory || '').trim()
    ? KIND.ASSET
    : KIND.ASSET_WILDCARD
}

const useLoadListBuilder = ({ loadListID, cloneFromId, prefillType }) => {
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [saving, setSaving] = useState(false)

  const [title, setTitle] = useState('')
  const [type, setType] = useState(prefillType || 'Vehicle')
  const [sections, setSections] = useState([])
  const [archived, setArchived] = useState(false)

  // assignments: [{ _id, kind, label, subLabel, level, lastCheckedAt,
  //                 pending: null | 'add' | 'remove' }]
  const [assignments, setAssignments] = useState([])

  const [selection, setSelection] = useState(null) // { sectionId, chipId }
  const [activeSectionId, setActiveSectionId] = useState(null)

  const baselineRef = useRef(snapshot({ title: '', type: '', sections: [] }))
  const [baselineKey, setBaselineKey] = useState(baselineRef.current)

  const isNew = !loadListID

  const applyDocument = useCallback(
    (doc, { asClone } = {}) => {
      const nextTitle = asClone
        ? `${doc?.title || ''} (Copy)`
        : doc?.title || ''
      const nextType = doc?.type || prefillType || 'Vehicle'
      const nextSections = sectionsFromDocument(doc)
      setTitle(nextTitle)
      setType(nextType)
      setSections(nextSections)
      setArchived(!!doc?.archived)
      return { title: nextTitle, type: nextType, sections: nextSections }
    },
    [prefillType],
  )

  const resetBaseline = useCallback((state) => {
    baselineRef.current = snapshot(state)
    setBaselineKey(baselineRef.current)
  }, [])

  const fetchAppliesTo = useCallback(async () => {
    if (!loadListID) return []
    const res = await API.get(`/makeready/loadlist/${loadListID}/applies-to`)
    if (res.status !== 200 || !Array.isArray(res.data)) return []
    return res.data.map((row) => ({ ...row, pending: null }))
  }, [loadListID])

  // ---- Initial load
  useEffect(() => {
    let mounted = true
    const load = async () => {
      setLoading(true)
      setFetchError(false)
      setSelection(null)
      try {
        const sourceId = loadListID || cloneFromId
        if (!sourceId) {
          const fresh = {
            title: '',
            type: prefillType || 'Vehicle',
            sections: [],
          }
          if (!mounted) return
          setTitle(fresh.title)
          setType(fresh.type)
          setSections(fresh.sections)
          setAssignments([])
          resetBaseline(fresh)
          return
        }
        const [docRes, applies] = await Promise.all([
          API.get(`/makeready/loadlist/${sourceId}`),
          fetchAppliesTo(),
        ])
        if (!mounted) return
        if (docRes.status !== 200 || !docRes.data) {
          setFetchError(true)
          return
        }
        const state = applyDocument(docRes.data, {
          asClone: !loadListID && !!cloneFromId,
        })
        setAssignments(loadListID ? applies : [])
        resetBaseline(state)
      } catch {
        if (mounted) setFetchError(true)
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [
    loadListID,
    cloneFromId,
    prefillType,
    applyDocument,
    fetchAppliesTo,
    resetBaseline,
  ])

  // ---- Derived
  const currentKey = useMemo(
    () => snapshot({ title, type, sections }),
    [title, type, sections],
  )
  const contentDirty = currentKey !== baselineKey
  const assignmentChanges = useMemo(
    () => assignments.filter((a) => a.pending).length,
    [assignments],
  )
  const dirty = contentDirty || assignmentChanges > 0

  const counts = useMemo(
    () => ({
      sections: sections.length,
      consumables: sections.reduce(
        (t, s) => t + s.requiredConsumables.length,
        0,
      ),
      assets: sections.reduce((t, s) => t + s.requiredAssets.length, 0),
      appliesTo: assignments.filter((a) => a.pending !== 'remove').length,
    }),
    [sections, assignments],
  )

  const findSection = useCallback(
    (sectionId) => sections.find((s) => s.clientId === sectionId) || null,
    [sections],
  )

  const selectedChip = useMemo(() => {
    if (!selection) return null
    const section = sections.find((s) => s.clientId === selection.sectionId)
    if (!section) return null
    for (const row of [ROW.CONSUMABLES, ROW.ASSETS]) {
      const chip = section[row].find((c) => c.clientId === selection.chipId)
      if (chip) return { section, row, chip, kind: kindForChip(row, chip) }
    }
    return null
  }, [selection, sections])

  // ---- Sections
  const addSection = useCallback((sectionName = '', sectionType = '') => {
    const next = makeSection(sectionName, sectionType)
    setSections((prev) => [...prev, next])
    setActiveSectionId(next.clientId)
    return next.clientId
  }, [])

  const updateSection = useCallback((sectionId, patch) => {
    setSections((prev) =>
      prev.map((s) => (s.clientId === sectionId ? { ...s, ...patch } : s)),
    )
  }, [])

  const removeSection = useCallback((sectionId) => {
    setSections((prev) => prev.filter((s) => s.clientId !== sectionId))
    setSelection((sel) => (sel?.sectionId === sectionId ? null : sel))
    setActiveSectionId((id) => (id === sectionId ? null : id))
  }, [])

  const reorderSections = useCallback((activeSectionId, overSectionId) => {
    setSections((prev) => {
      const from = prev.findIndex((s) => s.clientId === activeSectionId)
      const to = prev.findIndex((s) => s.clientId === overSectionId)
      if (from === -1 || to === -1 || from === to) return prev
      return arrayMove(prev, from, to)
    })
  }, [])

  // ---- Chips
  const makeChip = (kind, payload) =>
    kind === KIND.CONSUMABLE
      ? normaliseConsumable(payload)
      : normaliseAsset({
          ...payload,
          subCategory: kind === KIND.ASSET_WILDCARD ? '' : payload?.subCategory,
        })

  // Append (or insert before `beforeChipId`) a fresh chip built from a
  // palette payload. Returns the new chip's clientId.
  const addChip = useCallback((sectionId, kind, payload, beforeChipId) => {
    const row = rowForKind(kind)
    const chip = makeChip(kind, payload)
    setSections((prev) =>
      prev.map((s) => {
        if (s.clientId !== sectionId) return s
        const list = [...s[row]]
        const idx = beforeChipId
          ? list.findIndex((c) => c.clientId === beforeChipId)
          : -1
        if (idx === -1) list.push(chip)
        else list.splice(idx, 0, chip)
        return { ...s, [row]: list }
      }),
    )
    setSelection({ sectionId, chipId: chip.clientId })
    setActiveSectionId(sectionId)
    return chip.clientId
  }, [])

  const updateChip = useCallback((sectionId, chipId, patch) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.clientId !== sectionId) return s
        const next = { ...s }
        for (const row of [ROW.CONSUMABLES, ROW.ASSETS]) {
          if (s[row].some((c) => c.clientId === chipId)) {
            next[row] = s[row].map((c) =>
              c.clientId === chipId ? { ...c, ...patch } : c,
            )
          }
        }
        return next
      }),
    )
  }, [])

  const removeChip = useCallback((sectionId, chipId) => {
    setSections((prev) =>
      prev.map((s) =>
        s.clientId !== sectionId
          ? s
          : {
              ...s,
              requiredConsumables: s.requiredConsumables.filter(
                (c) => c.clientId !== chipId,
              ),
              requiredAssets: s.requiredAssets.filter(
                (c) => c.clientId !== chipId,
              ),
            },
      ),
    )
    setSelection((sel) => (sel?.chipId === chipId ? null : sel))
  }, [])

  // Move a chip within its section (reorder) or to another section
  // (optionally before a specific chip of the same row).
  const moveChip = useCallback(
    ({ fromSectionId, chipId, toSectionId, beforeChipId }) => {
      setSections((prev) => {
        const source = prev.find((s) => s.clientId === fromSectionId)
        const target = prev.find((s) => s.clientId === toSectionId)
        if (!source || !target) return prev
        const row = [ROW.CONSUMABLES, ROW.ASSETS].find((r) =>
          source[r].some((c) => c.clientId === chipId),
        )
        if (!row) return prev
        const chip = source[row].find((c) => c.clientId === chipId)

        if (fromSectionId === toSectionId) {
          if (!beforeChipId || beforeChipId === chipId) return prev
          const from = source[row].findIndex((c) => c.clientId === chipId)
          const to = source[row].findIndex((c) => c.clientId === beforeChipId)
          if (to === -1) return prev
          return prev.map((s) =>
            s.clientId !== fromSectionId
              ? s
              : { ...s, [row]: arrayMove(s[row], from, to) },
          )
        }

        return prev.map((s) => {
          if (s.clientId === fromSectionId) {
            return { ...s, [row]: s[row].filter((c) => c.clientId !== chipId) }
          }
          if (s.clientId === toSectionId) {
            const list = [...s[row]]
            const idx = beforeChipId
              ? list.findIndex((c) => c.clientId === beforeChipId)
              : -1
            if (idx === -1) list.push(chip)
            else list.splice(idx, 0, chip)
            return { ...s, [row]: list }
          }
          return s
        })
      })
      setSelection({ sectionId: toSectionId, chipId })
      setActiveSectionId(toSectionId)
    },
    [],
  )

  // ---- Applies-to
  const addAssignment = useCallback((candidate) => {
    setAssignments((prev) => {
      const existing = prev.find((a) => a._id === candidate._id)
      if (existing) {
        // Re-adding something flagged for removal just cancels the removal
        return prev.map((a) =>
          a._id === candidate._id
            ? { ...a, pending: a.pending === 'remove' ? null : a.pending }
            : a,
        )
      }
      return [
        ...prev,
        {
          _id: candidate._id,
          kind: candidate.kind,
          label: candidate.label,
          subLabel: candidate.subLabel || '',
          level: candidate.level || null,
          lastCheckedAt: candidate.lastCheckedAt || null,
          pending: 'add',
        },
      ]
    })
  }, [])

  const removeAssignment = useCallback((id) => {
    setAssignments((prev) =>
      prev
        .map((a) => {
          if (a._id !== id) return a
          if (a.pending === 'add') return null
          return { ...a, pending: a.pending === 'remove' ? null : 'remove' }
        })
        .filter(Boolean),
    )
  }, [])

  // ---- Payload / validation / persistence
  const buildPayload = useCallback(
    () => ({
      loadListID: loadListID || undefined,
      title: title.trim(),
      type,
      sections: serialiseSections(sections),
      // Legacy top-level rows were merged into sections on load, so the
      // document-level array is always emptied on save.
      requiredAssets: [],
      appliesTo: assignments
        .filter((a) => a.pending !== 'remove')
        .map((a) => a._id),
    }),
    [loadListID, title, type, sections, assignments],
  )

  const validate = useCallback(() => {
    const issues = []
    if (!title.trim()) issues.push('A title is required.')
    if (!type) issues.push('A type is required.')
    if (sections.length === 0) issues.push('Add at least one section.')
    sections.forEach((s, i) => {
      const label = s.sectionName.trim() || `Section ${i + 1}`
      s.requiredConsumables.forEach((c, j) => {
        if (!c.name.trim())
          issues.push(
            `${label}: consumable ${j + 1} has no name${c.size ? ` (size "${c.size}")` : ''}.`,
          )
      })
      s.requiredAssets.forEach((a, j) => {
        if (!a.category.trim())
          issues.push(
            `${label}: expected asset ${j + 1} has no category${a.subCategory ? ` (sub-category "${a.subCategory}")` : ''}.`,
          )
      })
    })
    return issues
  }, [title, type, sections])

  // Returns { ok: true, data } | { ok: false, error }
  const save = useCallback(async () => {
    setSaving(true)
    try {
      const res = await API.post('/settings/makereadyloadlist', buildPayload())
      if (res.status === 200) {
        if (loadListID) {
          const state = applyDocument(res.data)
          resetBaseline(state)
          const applies = await fetchAppliesTo()
          setAssignments(applies)
        }
        return { ok: true, data: res.data }
      }
      const error =
        res.data?.error ||
        (res.data?.errorCode === 'duplicate'
          ? 'A load list with this title already exists'
          : 'Unable to save the load list. Please try again.')
      return { ok: false, status: res.status, error }
    } catch {
      return {
        ok: false,
        error: 'Unable to save the load list. Please try again.',
      }
    } finally {
      setSaving(false)
    }
  }, [buildPayload, loadListID, applyDocument, resetBaseline, fetchAppliesTo])

  const discard = useCallback(async () => {
    setSelection(null)
    if (!loadListID) {
      const fresh = { title: '', type: prefillType || 'Vehicle', sections: [] }
      setTitle(fresh.title)
      setType(fresh.type)
      setSections(fresh.sections)
      setAssignments([])
      resetBaseline(fresh)
      return
    }
    setLoading(true)
    try {
      const [docRes, applies] = await Promise.all([
        API.get(`/makeready/loadlist/${loadListID}`),
        fetchAppliesTo(),
      ])
      if (docRes.status === 200 && docRes.data) {
        const state = applyDocument(docRes.data)
        setAssignments(applies)
        resetBaseline(state)
      }
    } finally {
      setLoading(false)
    }
  }, [loadListID, prefillType, applyDocument, fetchAppliesTo, resetBaseline])

  const deleteList = useCallback(async () => {
    if (!loadListID) return { ok: false }
    const res = await API.delete(`/makereadyloadlist/${loadListID}`)
    return res.status === 200
      ? { ok: true }
      : { ok: false, error: res.data?.error || 'Unable to delete load list' }
  }, [loadListID])

  return {
    // status
    loading,
    fetchError,
    saving,
    isNew,
    archived,
    // fields
    title,
    setTitle,
    type,
    setType,
    sections,
    assignments,
    counts,
    dirty,
    contentDirty,
    assignmentChanges,
    // selection
    selection,
    setSelection,
    selectedChip,
    activeSectionId,
    setActiveSectionId,
    findSection,
    // sections
    addSection,
    updateSection,
    removeSection,
    reorderSections,
    // chips
    addChip,
    updateChip,
    removeChip,
    moveChip,
    // applies-to
    addAssignment,
    removeAssignment,
    // persistence
    buildPayload,
    validate,
    save,
    discard,
    deleteList,
  }
}

export default useLoadListBuilder
